import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Loader from 'components/Loader';
import { HeaderBar, getCameraActions } from 'components/HeaderBar';
import PageLayout from 'components/PageLayout';
import { useMapState } from 'state/useMapState';
import { useAuthState } from 'state/useAuthState';
import { useGeoEventQuery } from 'features/geoEvents/hooks/useGeoEventQuery';
import { Location } from 'features/locations/location.types';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { useSelectedCameraId } from 'hooks/useSelectedCameraId';
import { useAlertCameraPlayerState } from 'features/cameras/hooks/useAlertCameraPlayerState';
import SupportUsIcon from 'assets/header-heart.svg?react';
import { useMapURLParams } from 'components/IncidentsMap/useMapURLParams';
import HomeContent from './HomeContent';
import { useHandleExternalPaymentSuccess } from './useHandleExternalPaymentSuccess';
import { getAdminMenuItems } from './Home.utils';
import { useAppUpgradeNotification } from '../../../hooks/useAppUpgradeNotification';

export const Home = (): JSX.Element => {
  const {
    permissions: { canReport, isRxOnlyReporter, canCreateLocation },
    showSupportUs,
  } = useAuthState();
  const { isPending } = useHandleExternalPaymentSuccess();
  const { t } = useTranslation();
  const { geoEvent } = useGeoEventQuery<Wildfire | Location>();
  const { incidentMapState } = useMapState();
  const cameraId = useSelectedCameraId();
  const { timelapseFrame } = useAlertCameraPlayerState();
  const { latitudeParam, longitudeParam, zoomLevelParam } = useMapURLParams();

  useAppUpgradeNotification();

  const hasEditPermissions = useMemo(() => {
    if (!geoEvent) return false;
    if (geoEvent.geoEventType !== 'wildfire') {
      return canReport && !isRxOnlyReporter;
    }
    if (!isRxOnlyReporter) return true;
    return geoEvent.data.isPrescribed;
  }, [geoEvent, isRxOnlyReporter, canReport]);

  const geoEventId =
    geoEvent?.data.geoEventType === 'wildfire' ? geoEvent.id : undefined;
  const locationId =
    geoEvent?.data.geoEventType === 'location' ? geoEvent.id : undefined;

  const automatedGeoEvent = !geoEvent?.reporterManaged;

  const showAdminActions = canReport && (!geoEventId || hasEditPermissions);

  const adminMenuItems = getAdminMenuItems(t, {
    automatedGeoEvent,
    geoEventId,
    locationId,
    geoEventMapState: geoEvent
      ? { lat: geoEvent.lat, lng: geoEvent.lng }
      : null,
    canCreateLocation,
    incidentMapState: {
      ...incidentMapState,
      center: {
        ...incidentMapState.center,
        lat: latitudeParam ?? incidentMapState.center.lat,
        lng: longitudeParam ?? incidentMapState.center.lng,
      },
      zoom: zoomLevelParam ?? incidentMapState.zoom,
    },
    timelapseFrame,
    cameraId,
  });

  const cameraAction = showAdminActions
    ? []
    : getCameraActions(
        t,
        geoEventId ? `/add_report/${geoEventId}` : '/add_report',
      );

  const iconActions = showSupportUs
    ? [
        {
          label: t('header.actions.supportUs'),
          to: {
            pathname: '/support_us',
            state: {
              fromRoute: window.location.pathname + window.location.search,
            },
          },
          Icon: <SupportUsIcon />,
        },
        ...cameraAction,
      ]
    : undefined;

  return (
    <PageLayout data-cname="HomePage" data-testid="homePage">
      {{
        content: (
          <>
            <HomeContent />
            {isPending && <Loader />}
          </>
        ),
        headerBar: (
          <HeaderBar
            iconActions={iconActions}
            menuActions={showAdminActions ? adminMenuItems : undefined}
          />
        ),
      }}
    </PageLayout>
  );
};
